import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarketingLink from '../MarketingLink/MarketingLink';
import restaurantService, { PlanFeaturesPayload, Restaurant } from '../../services/restaurantService';
import { isGuestDeliveryOpen, hasCustomerFeature, CUSTOMER_FEATURE_TABLE_BOOKING, CUSTOMER_FEATURE_TAKEAWAY } from '../../utils/planFeatures';
import { parseGuestOrderType, saveGuestOrderType } from '../../utils/guestOrderTypeStorage';
import deliveryService, { DeliveryConfig } from '../../services/deliveryService';
import { resolveRestaurantLogoUrl } from '../../constants/images';
import API_CONFIG from '../../config/api';
import { theme } from '../../theme/colors';
import {
  OpeningHoursRow,
  cuisineSubtitle,
  formatDayHourSlots,
  formatFullAddress,
  formatPhone,
  getDirectionsUrl,
  getTodayDayName,
} from './footerUtils';
import { FaIcon, FooterRatingStars } from './FooterIcons';
import {
  getPlatformReviewLinks,
  getSocialMediaLinks,
  hasSocialLinks,
} from '../../utils/restaurantSocialLinks';
import './footerIcons.css';
import './RestaurantFooter.css';

export interface RestaurantFooterProps {
  restaurant?: Restaurant | null;
  openingHours?: OpeningHoursRow[];
}

const RestaurantFooter: React.FC<RestaurantFooterProps> = ({
  restaurant: restaurantProp,
  openingHours: openingHoursProp,
}) => {
  const { appName: appNameParam } = useParams<{ appName: string }>();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(restaurantProp ?? null);
  const [openingHours, setOpeningHours] = useState<OpeningHoursRow[]>(openingHoursProp ?? []);
  const [planFeatures, setPlanFeatures] = useState<PlanFeaturesPayload | undefined>(undefined);
  const [deliveryConfig, setDeliveryConfig] = useState<DeliveryConfig | null>(null);

  useEffect(() => {
    if (restaurantProp) setRestaurant(restaurantProp);
  }, [restaurantProp]);

  useEffect(() => {
    if (openingHoursProp) setOpeningHours(openingHoursProp);
  }, [openingHoursProp]);

  useEffect(() => {
    if (!appNameParam) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await restaurantService.getRestaurant(appNameParam);
        if (cancelled || !res.success) return;
        setPlanFeatures(res.features);
        if (!restaurantProp && res.restaurant) setRestaurant(res.restaurant);
        if (!openingHoursProp && res.openingHours) setOpeningHours(res.openingHours);
      } catch {
        /* footer is non-critical */
      }
      try {
        const cfg = await deliveryService.getConfig(appNameParam);
        if (!cancelled) setDeliveryConfig(cfg);
      } catch {
        if (!cancelled) setDeliveryConfig(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appNameParam, restaurantProp, openingHoursProp]);

  const appName = restaurant?.appName || appNameParam;
  const year = new Date().getFullYear();
  const todayName = getTodayDayName();
  const baseUrl = API_CONFIG.baseURL || '';

  const handleShare = useCallback(async () => {
    if (!appName) return;
    const url = `${window.location.origin}/restaurants/${appName}`;
    const title = restaurant?.restaurantName || 'Restaurant';
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      /* user cancelled */
    }
  }, [appName, restaurant?.restaurantName]);

  if (!restaurant && !appNameParam) {
    return null;
  }

  const name = restaurant?.restaurantName || 'Restaurant';
  const tagline = restaurant?.appDesc?.trim();
  const phone = restaurant ? formatPhone(restaurant) : null;
  const email = restaurant?.restaurantAddressDetails?.emailId;
  const address = restaurant ? formatFullAddress(restaurant) : '';
  const manager = restaurant?.restaurantAddressDetails?.contactPerson;
  const fssaiNumber = restaurant?.fssaiNumber?.trim();
  const gstin = restaurant?.gstin?.trim();
  const directionsUrl = restaurant ? getDirectionsUrl(restaurant) : null;
  const logoSrc = restaurant?.id ? resolveRestaurantLogoUrl(restaurant) : null;
  const socialLinks = restaurant?.socialLinks;
  const platformLinks = getPlatformReviewLinks(socialLinks);
  const socialMediaLinks = getSocialMediaLinks(socialLinks);
  const showSocialSection = hasSocialLinks(socialLinks) || !!phone;

  const quickLinks = appName
    ? [
        { icon: 'list-alt', label: 'View Menu', to: `/restaurants/${appName}/menu` },
        ...(hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_TABLE_BOOKING)
          ? [{ icon: 'calendar', label: 'Reserve a Table', to: `/restaurants/${appName}/reserve-table` }]
          : []),
        ...(hasCustomerFeature(planFeatures, CUSTOMER_FEATURE_TAKEAWAY)
          ? [{ icon: 'shopping-bag', label: 'Take Away', to: `/restaurants/${appName}/menu?type=3` }]
          : []),
        ...(isGuestDeliveryOpen(planFeatures, deliveryConfig)
          ? [{ icon: 'bicycle', label: 'Delivery', to: `/restaurants/${appName}/menu?type=2` }]
          : []),
        { icon: 'star', label: 'Reviews', to: `/restaurants/${appName}` },
      ]
    : [];

  const platformQuickLinks = platformLinks.map((link) => ({
    icon: 'external-link',
    label:
      link.rating != null && link.rating > 0
        ? `${link.label} (${link.rating.toFixed(1)})`
        : link.label,
    href: link.url,
  }));

  return (
    <footer id="footer" className="footer-restaurant">
      <div className="fr-texture" aria-hidden />
      <div className="fr-inner">
        <div className="fr-top">
          <div className="fr-brand">
            <div className="fr-logo-block">
              <div className="fr-logo-mark">
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt=""
                    className="fr-logo-img"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                      fallback?.classList.remove('fr-logo-fallback-icon--hidden');
                    }}
                  />
                ) : null}
                <FaIcon
                  icon="cutlery"
                  className={`fr-logo-fallback-icon${logoSrc ? ' fr-logo-fallback-icon--hidden' : ''}`}
                />
              </div>
              <div>
                <div className="fr-logo-name">{name}</div>
                <div className="fr-logo-sub">{restaurant ? cuisineSubtitle(restaurant) : ''}</div>
              </div>
            </div>
            {tagline && <p className="fr-tagline">&ldquo;{tagline}&rdquo;</p>}
            {restaurant?.cuisineList && restaurant.cuisineList.length > 0 && (
              <div className="fr-cuisine-tags">
                {restaurant.cuisineList.map((c) => (
                  <span key={c.id} className="fr-tag">
                    {c.name}
                  </span>
                ))}
              </div>
            )}
            <div className="fr-brand-row">
              {restaurant?.rating != null && restaurant.rating > 0 && (
                <div className="fr-rating">
                  <FooterRatingStars rating={restaurant.rating} className="fr-rating-stars" />
                  <span className="fr-rating-val">{restaurant.rating.toFixed(1)}</span>
                  <span className="fr-rating-count">/ 5</span>
                </div>
              )}
              <MarketingLink href="/" className="fr-powered" aria-label="Powered by Peppar">
                <span className="fr-powered-label">Powered by</span>
                <span className="fr-powered-badge">
                  <img src={theme.logo.url} alt="Peppar" className="fr-powered-logo" />
                </span>
              </MarketingLink>
            </div>
          </div>

          <div>
            <div className="fr-col-title">Quick Links</div>
            <ul className="fr-links">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    onClick={() => {
                      const type = parseGuestOrderType(
                        new URLSearchParams(link.to.split('?')[1] || '').get('type')
                      );
                      if (appName && type != null) {
                        saveGuestOrderType(appName, type);
                      }
                    }}
                  >
                    <FaIcon icon={link.icon} className="fr-link-icon" />
                    {link.label}
                  </Link>
                </li>
              ))}
              {platformQuickLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} target="_blank" rel="noopener noreferrer">
                    <FaIcon icon={link.icon} className="fr-link-icon" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {openingHours.length > 0 && (
            <div className="fr-hours">
              <div className="fr-col-title">Opening Hours</div>
              {openingHours.map((hours) => {
                const isToday = hours.day.toLowerCase() === todayName.toLowerCase();
                const slots = formatDayHourSlots(hours);
                const isClosed = slots.length === 0;
                return (
                  <div key={hours.day} className={`fr-hour-row${isToday ? ' today' : ''}`}>
                    <span className="fr-hour-day">
                      {hours.day}
                      {isToday ? ' (Today)' : ''}
                    </span>
                    <span className={`fr-hour-time${isClosed ? ' closed' : ''}`}>
                      {isClosed
                        ? 'Closed'
                        : slots.map((slot) => (
                            <span key={slot} className="fr-hour-slot">
                              {slot}
                            </span>
                          ))}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <div className="fr-col-title">Contact Us</div>
            <div className="fr-contact-list">
              {phone && (
                <div className="fr-contact-row">
                  <div className="fr-contact-icon">
                    <FaIcon icon="phone" />
                  </div>
                  <div>
                    <div className="fr-contact-label">Phone</div>
                    <div className="fr-contact-value">
                      <a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a>
                    </div>
                  </div>
                </div>
              )}
              {email && (
                <div className="fr-contact-row">
                  <div className="fr-contact-icon">
                    <FaIcon icon="envelope-o" />
                  </div>
                  <div>
                    <div className="fr-contact-label">Email</div>
                    <div className="fr-contact-value">
                      <a href={`mailto:${email}`}>{email}</a>
                    </div>
                  </div>
                </div>
              )}
              {address && (
                <div className="fr-contact-row">
                  <div className="fr-contact-icon">
                    <FaIcon icon="map-marker" />
                  </div>
                  <div>
                    <div className="fr-contact-label">Address</div>
                    <div className="fr-contact-value">{address}</div>
                  </div>
                </div>
              )}
              {manager && (
                <div className="fr-contact-row">
                  <div className="fr-contact-icon">
                    <FaIcon icon="user" />
                  </div>
                  <div>
                    <div className="fr-contact-label">Manager</div>
                    <div className="fr-contact-value">{manager}</div>
                  </div>
                </div>
              )}
            </div>
            {showSocialSection && (
              <div className="fr-social" aria-label="Social media">
                {socialMediaLinks.map((link) => (
                  <a
                    key={link.key}
                    href={link.url}
                    className={`fp-social-btn fr-social-${link.key}`}
                    title={link.label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaIcon icon={link.icon} />
                  </a>
                ))}
                {phone && (
                  <a
                    href={`https://wa.me/${phone.replace(/\D/g, '')}`}
                    className="fp-social-btn fr-social-wa"
                    title="WhatsApp"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaIcon icon="whatsapp" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="fr-bottom">
          <div className="fr-copy">
            © {year} {name}. All Rights Reserved. Powered by <MarketingLink href="/">Peppar</MarketingLink>
            {(fssaiNumber || gstin) && (
              <div className="fr-statutory">
                {fssaiNumber && <span>FSSAI Lic. No. {fssaiNumber}</span>}
                {gstin && <span>GSTIN {gstin}</span>}
              </div>
            )}
          </div>
          <div className="fr-actions">
            {directionsUrl && (
              <a href={directionsUrl} className="fr-action-btn" target="_blank" rel="noopener noreferrer">
                <FaIcon icon="location-arrow" className="fr-action-icon" />
                Get Directions
              </a>
            )}
            <button type="button" className="fr-action-btn" onClick={handleShare}>
              <FaIcon icon="share-alt" className="fr-action-icon" />
              Share
            </button>
            {appName && (
              <Link to={`/restaurants/${appName}/menu`} className="fr-action-btn primary">
                <FaIcon icon="list-alt" className="fr-action-icon" />
                View Full Menu
              </Link>
            )}
          </div>
          <div className="fr-legal">
            <a href={`${baseUrl}/terms-and-conditions`} target="_blank" rel="noopener noreferrer">
              Terms
            </a>
            <a href={`${baseUrl}/privacy-and-policy`} target="_blank" rel="noopener noreferrer">
              Privacy
            </a>
            <a href="mailto:contact@peppar.in">Feedback</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default RestaurantFooter;
